// iOS install-gate surface classifier (Story 7.1 spike).
//
// There is no direct browser API for "is the address bar present" / "is this a real
// installed PWA" — it is INFERRED from three signals. This module is the single source
// of truth for that inference (mirrors the route-access.ts "one canonical module" rule).
//
// Why it matters: on iOS each surface (Safari proper, Chrome iOS, a home-screen bookmark,
// and a manifest-driven PWA) gets its OWN isolated IndexedDB bucket, and only the
// manifest-driven PWA is granted durable persistent storage. Offline clinical work is
// only safe on `pwa-proper`; every other iOS surface must be gated to the install flow so
// a clinician does not lose an evaluation to a wiped/fragile bucket.
//
// Truth table (verified on a real iPhone in the R6 derisking PoC — see Dev Notes):
//
//   Surface                | iOS? | display-mode standalone | navigator.standalone | UA marker
//   -----------------------|------|-------------------------|----------------------|----------
//   safari-proper          | yes  | false                   | false                | Safari (no alt-browser token)
//   pwa-proper             | yes  | true                    | true                 | —
//   home-screen-bookmark   | yes  | false                   | true                 | —
//   chrome-ios             | yes  | false                   | false                | CriOS / FxiOS / EdgiOS
//   other                  | no   | n/a                     | undefined            | not iOS
//
// Decisive tiebreaker: `pwa-proper` and `home-screen-bookmark` BOTH report
// navigator.standalone === true; only `pwa-proper` additionally reports
// display-mode: standalone === true (it has a real manifest, shipped in Story 7.2).

export type Surface = "safari-proper" | "pwa-proper" | "chrome-ios" | "home-screen-bookmark" | "other";

// navigator.standalone is a non-standard, iOS-Safari-only boolean. Off iOS it is
// genuinely `undefined`, so it is typed optional and read defensively.
type IosNavigator = Navigator & { standalone?: boolean };

/** True for iPhone/iPod/iPad — including iPadOS 13+, which reports a desktop
 * "Macintosh" UA and can only be told apart by its touch points. */
function isIos(ua: string, maxTouchPoints: number): boolean {
  if (/iP(hone|ad|od)/.test(ua)) return true;
  // iPadOS-as-Mac: a Macintosh UA with a touch screen is an iPad, not a desktop Mac.
  return /Macintosh/.test(ua) && maxTouchPoints > 1;
}

/** Read `display-mode: standalone` defensively. matchMedia may be absent (SSR/jsdom) or,
 * on some locked-down iOS WKWebViews, throw when called — in either case treat the surface
 * as not-standalone rather than letting the exception escape into React render. */
function isDisplayModeStandalone(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

/**
 * Classify the current surface as exactly one of the {@link Surface} values.
 *
 * Pure: it only READS globals (defensively — matchMedia and navigator.standalone may be
 * absent) and has no side effects, so it is fully unit-testable with mocked globals.
 */
export function detectSurface(): Surface {
  const nav = typeof navigator !== "undefined" ? (navigator as IosNavigator) : undefined;
  const ua = nav?.userAgent ?? "";
  const maxTouchPoints = nav?.maxTouchPoints ?? 0;

  // Non-iOS (desktop, Android, iPadOS reporting desktop UA without touch) is never gated.
  if (!isIos(ua, maxTouchPoints)) return "other";

  // display-mode: standalone is the distinguishing signal for a manifest-driven PWA.
  if (isDisplayModeStandalone()) return "pwa-proper";

  // navigator.standalone true while display-mode is false ⇒ a home-screen bookmark
  // (no manifest): standalone chrome, but a fragile, non-persistent storage bucket.
  if (nav?.standalone === true) return "home-screen-bookmark";

  // Both standalone signals false: in-tab. Any non-Safari iOS browser is a WKWebView
  // wrapper that cannot "Add to Home Screen" — Chrome (`CriOS`), Firefox (`FxiOS`), Edge
  // (`EdgiOS`). Classify them as `chrome-ios` (the "non-Safari iOS in-tab browser" bucket,
  // within AC1's 5-surface enum) so the gate shows the "open in Safari first" caution.
  // In-app webviews (FBAN/FBAV/Instagram/GSA…) have less stable UA tokens — they currently
  // fall through to `safari-proper` and are flagged for the real-device matrix follow-up.
  if (/CriOS|FxiOS|EdgiOS/.test(ua)) return "chrome-ios";
  return "safari-proper";
}
